import { useEffect } from "react";
import { mutate } from "swr";
import { useCallStore } from "@/frontend/stores/callStore";
import { useMemberStore } from "@/frontend/stores/memberStore";

type CallUpdate = { callId: string; status: string; direction: string };

// One stream per tab, so a call rings wherever the teammate is in the app. An open stream is also what
// makes the teammate available to be rung.
export function useCallEvents(orgId: string | undefined) {
  const canRead = useMemberStore((state) => state.fetched && state.canRead("calls"));
  const callingAvailable = useCallStore((state) => state.callingAvailable);

  useEffect(() => {
    // Switching workspace drops the previous one's calls, even where this one has no Calls access
    useCallStore.getState().setOrg(orgId ?? null);
    if (!orgId || !canRead) return;
    void useCallStore.getState().loadAvailability();
  }, [orgId, canRead]);

  useEffect(() => {
    // Without a WhatsApp Business number nothing can ring, so there is nothing to listen for
    if (!orgId || !canRead || !callingAvailable || typeof window === "undefined") return;
    const store = useCallStore.getState();
    void store.loadAway();
    void store.refreshMissed();
    const source = new EventSource(`/api/calls/events?organizationId=${encodeURIComponent(orgId)}`, { withCredentials: true });
    const handleUpdate = (rawEvent: MessageEvent) => {
      let update: CallUpdate;
      try {
        update = JSON.parse(rawEvent.data) as CallUpdate;
      } catch {
        return;
      }
      const canAnswer = useMemberStore.getState().canWrite("calls");
      void useCallStore.getState().handleUpdate(update, canAnswer);
      // Call log and thread lists key their data by /api/calls URLs
      void mutate((key) => typeof key === "string" && key.startsWith("/api/calls?"));
    };
    const handlePermission = (rawEvent: MessageEvent) => {
      try {
        useCallStore.getState().handlePermissionReply(JSON.parse(rawEvent.data) as { conversationId: string; granted: boolean });
      } catch {
        // A malformed event is ignored
      }
    };
    source.addEventListener("call.updated", handleUpdate);
    source.addEventListener("call.permission", handlePermission);
    // Fires on connect and every reconnect, which may have missed a ring
    source.addEventListener("ready", () => void useCallStore.getState().loadRinging());
    const handlePageHide = () => useCallStore.getState().endOnUnload();
    // Leaving the page hangs up, so ask first
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!useCallStore.getState().active) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      source.removeEventListener("call.updated", handleUpdate);
      source.removeEventListener("call.permission", handlePermission);
      source.close();
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [orgId, canRead, callingAvailable]);
}
